









































































import { Component, Vue, Prop, Watch } from "vue-property-decorator";
import * as _ from "lodash";
import { DataOptions } from "vuetify";
import { SortableTableHeader } from "./SortableTableDirective";
import RightDrawer from "@/components/misc/RightDrawer.vue";

class GenericPMSTableSettingsModel {
  columns: Column[] = [];
  options?: DataOptions;
  expandedRow: Column | null = null;
}

type Column = {
  value: string;
  active: boolean;
  index: number;
  width: string | number | undefined;
};

@Component({
  components: {
    RightDrawer,
  },
})
export default class GenericPMSTableMenu extends Vue {
  @Prop({ default: false }) open!: boolean;

  @Prop() columns!: SortableTableHeader[];
  @Prop({ required: true }) options!: DataOptions;
  @Prop({ default: null }) expandedRow!: SortableTableHeader | null;
  @Prop() tableName!: string;
  @Prop({ default: false }) hasExportToExcel?: boolean;
  @Prop({ default: false }) loadSettingsOnTableChange?: boolean;

  get _columns() {
    return this.columns;
  }
  set _columns(val) {
    this.$emit("update:columns", val);
  }
  @Watch("columns")
  columnsWatch(fresh: SortableTableHeader[], old: SortableTableHeader[]) {
    const resertWidths = fresh.some(
      (f) => !old.find((o) => o.value == f.value)?.active && f.active
    );

    this.onColumnsChange(resertWidths);
  }

  get _options() {
    return this.options;
  }
  set _options(val) {
    this.$emit("update:options", val);
  }
  @Watch("options")
  optionsWatch(fresh: any, old: any) {
    if (!_.isEqual(fresh, old)) {
      this.tableSettings.options = this._options;
      this.saveSettings();
    }
  }

  get _expandedRow() {
    return this.expandedRow;
  }
  set _expandedRow(val) {
    this.$emit("update:expandedRow", val);
  }

  tableSettings!: GenericPMSTableSettingsModel;

  // defaults
  defaultColumns!: SortableTableHeader[];
  defaultOptions!: DataOptions;
  defaultExpandedRow!: SortableTableHeader | null;

  localColumns: SortableTableHeader[] = [];
  localExpandedRow: SortableTableHeader | null = null;

  @Watch("open", { immediate: true })
  openWatch() {
    this.localColumns = _.cloneDeep(this._columns);
    this.localExpandedRow = _.cloneDeep(this._expandedRow);
  }

  @Watch("tableName")
  tableNameWatch(fresh: string, old: string) {
    if (fresh !== old && this.loadSettingsOnTableChange) {
      this.loadSettings();
      this.localColumns = _.cloneDeep(this._columns);
      this.localExpandedRow = _.cloneDeep(this._expandedRow);
    }
  }

  mounted() {
    // remember defaults
    this.defaultColumns = _.cloneDeep(this._columns);
    this.defaultOptions = _.cloneDeep(this._options);
    this.defaultExpandedRow = this._expandedRow
      ? _.cloneDeep(this._expandedRow)
      : null;

    this.loadSettings();
  }

  loadSettings() {
    // get table settings from local storage
    const utid = this.$store.getters["auth/claims"]?.utid;
    const userTableSettings = JSON.parse(
      localStorage.getItem("pms.utid_" + utid + "_tableSettings") || "{}"
    );

    const tableSettings =
      (userTableSettings[this.tableName] as GenericPMSTableSettingsModel) ||
      new GenericPMSTableSettingsModel();

    if (!tableSettings.options) {
      tableSettings.options = this._options;
    }

    tableSettings.options.multiSort = false;
    tableSettings.options.mustSort = true;

    this.tableSettings = tableSettings;

    // set column status and order
    if (this.tableSettings.columns) {
      let temp = Array<any>();
      let i = 0;
      for (const column of this._columns) {
        const storedColumn = this.tableSettings.columns.find(
          (ac) => ac.value == column.value
        );

        if (!column.default) {
          column.active = storedColumn?.active ?? column.active;
        }

        column.width = storedColumn?.width;

        temp.push({
          index: storedColumn
            ? storedColumn.index
            : this.tableSettings.columns.length + i++,
          item: column,
        });
      }
      temp = temp.sort((a, b) => a.index - b.index);
      this._columns = temp.map((x) => x.item).slice();
    }

    // set expanded row status
    if (this.tableSettings.expandedRow && this._expandedRow) {
      const temp = this._expandedRow;
      temp.active = this.tableSettings.expandedRow.active;
      this._expandedRow = temp;
    }

    // set table options - sort etc.
    if (this.tableSettings.options) {
      this.tableSettings.options.page = 1;
      this._options = this.tableSettings.options;
    }

    this.$emit("loaded:settings");
  }

  onColumnsChange(resetWidths?: boolean) {
    // reset column sizes when a hidden column is shown
    // prevents breaking of table in certain cases
    if (resetWidths) {
      this._columns.forEach((c) => (c.width = undefined));
    }

    this.tableSettings.columns = this._columns.map((item, index) => {
      return {
        value: item.value,
        active: item.active,
        index: index,
        width: item.width,
      };
    });

    if (this._expandedRow) {
      this.tableSettings.expandedRow = {
        value: this._expandedRow.value,
        active: this._expandedRow.active,
        width: undefined,
        index: 0,
      };
    } else {
      this.tableSettings.expandedRow = null;
    }

    if (this.tableSettings.options && this.tableSettings.options.sortBy) {
      this.tableSettings.options.sortBy =
        this.tableSettings.options.sortBy.filter(
          (x) =>
            this.tableSettings.columns.find((c) => c.value == x)?.active ??
            false
        );
    }

    this.saveSettings();
  }

  saveSettings() {
    const utid = this.$store.getters["auth/claims"]?.utid;
    const userTableSettings = JSON.parse(
      localStorage.getItem("pms.utid_" + utid + "_tableSettings") || "{}"
    );
    userTableSettings[this.tableName] = this.tableSettings;
    localStorage.setItem(
      `pms.utid_${utid}_tableSettings`,
      JSON.stringify(userTableSettings)
    );
  }

  applySettings() {
    this._columns = _.cloneDeep(this.localColumns);
    this._expandedRow = _.cloneDeep(this.localExpandedRow);
    this.$emit("update:open", false);
  }

  resetToDefaultSettings() {
    this._columns = _.cloneDeep(this.defaultColumns);
    this._options = _.cloneDeep(this.defaultOptions);
    this._expandedRow = this.defaultExpandedRow
      ? _.cloneDeep(this.defaultExpandedRow)
      : null;
    this.$emit("update:open", false);
  }

  exportToExcel() {
    this.$emit("menu:excel");
  }
}
